import React, { useState } from "react"
import {
  useDocumentsMutation,
  useDocumentsQuery
} from "../../queries/useDocuments"
import { useQueryClient } from "@tanstack/react-query"

type Props = {
  patientId: string
  goNext: () => void
}

type ReportType = "MRI_REPORT" | "SCANNER_REPORT" | "OTHER"

const reportTypesDisplay = [
  { key: "MRI_REPORT", label: "Compte-rendu IRM" },
  { key: "SCANNER_REPORT", label: "Compte-rendu Scanner" },
  { key: "OTHER", label: "Autre" }
]

const FileUploader = (props: Props) => {
  const queryClient = useQueryClient()
  const [file, setFile] = useState<File | null>(null)
  const [comment, setComment] = useState<string>("")
  const [reportType, setReportType] = useState<ReportType>()

  const { data: medicalDocuments, isLoading } = useDocumentsQuery(
    props.patientId
  )

  const { mutateAsync, isPending } = useDocumentsMutation(props.patientId)
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFile(e.target.files[0])
    }
  }

  const handleUpload = async () => {
    if (file) {
      const formData = new FormData()
      formData.append("file", file)
      formData.append("document_type", reportType ?? "MRI_REPORT")
      formData.append("comment", comment)

      mutateAsync(formData).then(() => {
        queryClient.invalidateQueries({ queryKey: ["medical_documents"] })
      })
    }
  }

  const onChangeReportType = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target
    setReportType(value as ReportType)
  }

  return (
    <div className="flex flex-col items-center justify-center h-full w-full mx-4">
      {!medicalDocuments?.length ? (
        <div className="flex flex-col overflow-auto items-center justify-center h-full w-full lg:w-2/3 xl:w-1/2 xl:h-3/4 px-4 lg:px-8 py-2 border gap-4 rounded-xl">
          <h1 className="text-md font-bold text-cortexx-900 text-center">
            Pouvez-vous charger un Compte-Rendu d'IRM ou de Scanner de la zone
            concernée (lombaire ou cervicale) datant de moins de 6 mois ?
          </h1>
          <h2 className="font-normal text-cortexx-900 text-center">
            Cela facilite l'analyse de votre dossier par le chirurgien.
          </h2>
          <div className="flex flex-col w-full items-center justify-center mb-8">
            <form
              key={"document_upload"}
              className="flex flex-col items-start w-full"
            >
              <div className="mt-2 w-full">
                <label className="text-cortexx-900 text-sm text-start font-bold">
                  Quel type de document souhaitez-vous charger ?
                </label>
                <div className="flex flex-col mt-2 md:mt-3">
                  {reportTypesDisplay.map((option) => (
                    <div className="flex flex-row mr-8">
                      <input
                        type="checkbox"
                        name={option.label}
                        id={option.key}
                        value={option.key}
                        onChange={onChangeReportType}
                        checked={reportType === option.key}
                      />
                      <label className="ml-2 text-sm">{option.label}</label>
                    </div>
                  ))}
                </div>
              </div>
              <input
                className="justify-center text-sm text-cortexx-550 mt-4 file:mr-5 file:py-3 file:px-6 file:border file:border-cortexx-550 file:rounded-lg file:text-sm file:bg-cortexx-550 file:text-cortexx-50 hover:file:cursor-pointer hover:file:bg-cortexx-50 hover:file:text-cortexx-550 hover:file:border hover:file:border-cortexx-550"
                id="file_input"
                type="file"
                onChange={handleFileChange}
              />
              <div className="mt-4 w-full">
                <label className="text-cortexx-900 text-sm text-start font-bold">
                  Avez-vous un commentaire à ajouter au document ?
                </label>
                <input
                  type="text"
                  id="comment"
                  className="mt-2 border border-cortexx-550 rounded-md p-2 w-full text-sm"
                  placeholder="Entrez vos remarques"
                  onChange={(e) => setComment(e.target.value)}
                />
              </div>
            </form>
          </div>

          <div className="flex w-full justify-end items-start">
            {!isPending ? (
              <button
                onClick={handleUpload}
                disabled={!file}
                className="bg-cortexx-500 text-white font-semibold py-3 px-5 rounded-lg"
              >
                Envoyer le document
              </button>
            ) : (
              <button
                className="bg-cortexx-500 text-white font-semibold py-3 px-5 rounded-lg"
                disabled
              >
                <img
                  src="/images/fade-stagger-circles.svg"
                  style={{ height: "20px" }}
                  alt="close"
                  loading="lazy"
                />
              </button>
            )}
          </div>
        </div>
      ) : (
        <>
          <h1 className="text-lg font-normal text-cortexx-900 text-center mb-4">
            Vos documents ont bien été pris en compte
          </h1>
          <h2 className="font-normal text-cortexx-900 text-center">
            Vous pouvez passer à l'étape suivante
          </h2>
          <div className="mt-4">
            <button
              onClick={() => props.goNext()}
              className="bg-cortexx-500 text-white font-semibold py-3 px-5 rounded-lg"
            >
              Passer
            </button>
          </div>
        </>
      )}
    </div>
  )
}

export default FileUploader
